import { defineStore } from "pinia";
import { reactive, shallowRef, triggerRef } from "vue";
import { getOrganizationName } from "@/entities/organization/api";

const TTL_FRESH = 10 * 60_000;
const MAX_ENTRIES = 200;

export const useOrganizationNameCacheStore = defineStore(
  "organization-name-cache",
  () => {
    const entriesRef = shallowRef(new Map());
    const lru = [];

    function get(id) {
      return entriesRef.value.get(String(id));
    }

    function setEntry(id, entry) {
      entriesRef.value.set(String(id), entry);
      triggerRef(entriesRef);
    }

    function touch(id) {
      const key = String(id);
      const i = lru.indexOf(key);
      if (i > -1) lru.splice(i, 1);
      lru.unshift(key);
      while (lru.length > MAX_ENTRIES) {
        const evictKey = lru.pop();
        if (evictKey === key) continue;
        const ev = entriesRef.value.get(evictKey);
        if (ev?.controller) ev.controller.abort();
        entriesRef.value.delete(evictKey);
      }
      triggerRef(entriesRef);
    }

    function isFresh(entry) {
      return (
        entry?.status === "success" && Date.now() - entry.fetchedAt < TTL_FRESH
      );
    }

    function fetchName(id) {
      if (id == null) return Promise.resolve(null);
      const key = String(id);
      const existing = get(key);
      if (isFresh(existing)) {
        touch(key);
        return Promise.resolve(existing.data);
      }
      if (existing?.promise) {
        touch(key);
        return existing.promise;
      }

      const controller =
        typeof AbortController !== "undefined" ? new AbortController() : null;

      const entry = reactive({
        data: existing?.data ?? null,
        status: existing?.data ? "success" : "loading",
        fetchedAt: existing?.fetchedAt ?? 0,
        promise: null,
        controller,
      });

      const promise = Promise.resolve()
        .then(() => getOrganizationName(id, { signal: controller?.signal }))
        .then((name) => {
          entry.data = name;
          entry.status = "success";
          entry.fetchedAt = Date.now();
          return name;
        })
        .catch((err) => {
          const aborted =
            err?.name === "AbortError" ||
            err?.name === "CanceledError" ||
            err?.code === "ERR_CANCELED";
          if (!aborted) entry.status = "error";
          throw err;
        })
        .finally(() => {
          entry.promise = null;
          entry.controller = null;
        });

      entry.promise = promise;
      setEntry(key, entry);
      touch(key);
      promise.catch(() => {});
      return promise;
    }

    return { entriesRef, fetchName, get };
  }
);
